import React, { useState, useEffect, useRef } from 'react';
import WelcomeScreen from './components/WelcomeScreen';
import ActivationCodeScreen from './components/ActivationCodeScreen';
import InstructionsScreen from './components/InstructionsScreen';
import LevelGradeSelectionScreen from './components/LevelGradeSelectionScreen';
import ReviewTypeSelectionScreen from './components/ReviewTypeSelectionScreen';
import TestScheduleScreen from './components/TestScheduleScreen';
import ReviewsListScreen from './components/ReviewsListScreen';
import FileViewerScreen from './components/FileViewerScreen';
import VisitorsScreen from './components/VisitorsScreen';
import { trackVisitor } from './services/visitorTrackingService';
import './App.css';

function App() {
  const [currentScreen, setCurrentScreen] = useState('welcome');
  const [selectedEducationLevel, setSelectedEducationLevel] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [viewerFile, setViewerFile] = useState(null);
  const [previousScreen, setPreviousScreen] = useState(null);
  const [showVisitorsScreen, setShowVisitorsScreen] = useState(false);
  const visitorsScreenRef = useRef(false);

  // التحقق من query parameter عند التحميل والاستماع للأحداث من script في index.html
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('visitors') === 'true' || urlParams.get('admin') === 'true') {
      visitorsScreenRef.current = true;
      setShowVisitorsScreen(true);
    }
    
    // التحقق من الحالة المحفوظة في window object من script في index.html
    if (window.__visitorsScreenFlag && window.__visitorsScreenFlag()) {
      visitorsScreenRef.current = true;
      setShowVisitorsScreen(true);
    }
    
    // الاستماع للأحداث المخصصة من script في index.html
    const handleOpenVisitors = () => {
      visitorsScreenRef.current = true;
      setShowVisitorsScreen(true);
    };
    
    window.addEventListener('openVisitorsScreen', handleOpenVisitors);
    document.addEventListener('openVisitorsScreen', handleOpenVisitors);
    
    // إضافة دالة عامة للوصول من console
    window.openVisitorsScreen = () => {
      visitorsScreenRef.current = true;
      setShowVisitorsScreen(true);
    };
    
    // إضافة دالة عامة لإغلاق الشاشة
    window.closeVisitorsScreen = () => {
      visitorsScreenRef.current = false;
      setShowVisitorsScreen(false);
    };
    
    return () => {
      window.removeEventListener('openVisitorsScreen', handleOpenVisitors);
      document.removeEventListener('openVisitorsScreen', handleOpenVisitors);
      delete window.openVisitorsScreen;
      delete window.closeVisitorsScreen;
    };
  }, []);

  // تمرير الصفحة للأعلى عند تغيير الشاشة أو فتح الموقع
  useEffect(() => {
    // استخدام setTimeout لضمان عمل scroll بعد render
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      // للمتصفحات القديمة
      if (window.pageYOffset) {
        window.pageYOffset = 0;
      }
    }, 0);
  }, [currentScreen]);

  // تمرير الصفحة للأعلى عند تحميل الموقع لأول مرة
  useEffect(() => {
    const scrollToTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    
    // عند تحميل الصفحة
    scrollToTop();
    
    // بعد تحميل الصفحة بالكامل
    window.addEventListener('load', scrollToTop);
    
    return () => {
      window.removeEventListener('load', scrollToTop);
    };
  }, []);

  // تتبع الزوار عند تحميل التطبيق
  useEffect(() => {
    trackVisitor();
  }, []);

  // مستمع لاختصار لوحة المفاتيح Alt + Shift + A (للكمبيوتر فقط)
  useEffect(() => {
    let keySequence = [];

    const openVisitors = () => {
      visitorsScreenRef.current = true;
      setShowVisitorsScreen(true);
    };

    const closeVisitors = () => {
      visitorsScreenRef.current = false;
      setShowVisitorsScreen(false);
    };

    const handleKeyDown = (event) => {
      // التحقق من Alt + Shift + A بطرق متعددة
      const key = event.key ? event.key.toLowerCase() : '';
      const keyCode = event.keyCode || event.which || 0;
      const isAlt = event.altKey || event.altLeft || event.altRight;
      const isShift = event.shiftKey || event.shiftLeft || event.shiftRight;
      const isA = key === 'a' || keyCode === 65;
      
      const isAltShiftA = isAlt && isShift && isA;
      
      if (isAltShiftA) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        openVisitors();
        return false;
      }
      
      // إغلاق الشاشة عند الضغط على Escape
      if (event.key === 'Escape' || keyCode === 27) {
        if (visitorsScreenRef.current) {
          event.preventDefault();
          closeVisitors();
        }
      }
    };

    const handleKeyUp = (event) => {
      // طريقة بديلة: تسلسل مفاتيح Alt + Shift + A
      if (event.altKey && event.shiftKey) {
        const key = event.key ? event.key.toLowerCase() : '';
        keySequence.push(key);
        if (keySequence.length > 3) {
          keySequence.shift();
        }
        if (key === 'a' && keySequence.length >= 1) {
          event.preventDefault();
          openVisitors();
          keySequence = [];
        }
      } else {
        keySequence = [];
      }
    };

    // إضافة مستمعات للأحداث على document و window مع capture phase
    const options = { capture: true, passive: false };
    
    // إضافة listeners متعددة للتأكد من العمل
    document.addEventListener('keydown', handleKeyDown, options);
    document.addEventListener('keyup', handleKeyUp, options);
    window.addEventListener('keydown', handleKeyDown, options);
    window.addEventListener('keyup', handleKeyUp, options);
    
    // إضافة listener عند تحميل الصفحة بالكامل
    const onLoadHandler = () => {
      document.addEventListener('keydown', handleKeyDown, options);
      window.addEventListener('keydown', handleKeyDown, options);
    };
    window.addEventListener('load', onLoadHandler);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown, options);
      document.removeEventListener('keyup', handleKeyUp, options);
      window.removeEventListener('keydown', handleKeyDown, options);
      window.removeEventListener('keyup', handleKeyUp, options);
      window.removeEventListener('load', onLoadHandler);
    };
  }, []); // إزالة showVisitorsScreen من dependencies لتجنب إعادة إنشاء listeners

  const handleBackToSelection = () => {
    setCurrentScreen('reviewType');
  };

  const handleReset = () => {
    setSelectedEducationLevel(null);
    setSelectedGrade(null);
    setSelectedType(null);
    setHasAcceptedTerms(false);
    setCurrentScreen('welcome');
  };

  return (
    <div className="App">
      {currentScreen === 'welcome' && (
        <WelcomeScreen onComplete={() => setCurrentScreen('activationCode')} />
      )}
      
      {currentScreen === 'activationCode' && (
        <ActivationCodeScreen
          onComplete={() => setCurrentScreen('instructions')}
          onBack={() => setCurrentScreen('welcome')}
        />
      )}
      
      {currentScreen === 'instructions' && (
        <InstructionsScreen
          hasAcceptedTerms={hasAcceptedTerms}
          onAccept={() => {
            setHasAcceptedTerms(true);
            setCurrentScreen('levelGrade');
          }}
          onBack={() => setCurrentScreen('activationCode')}
        />
      )}
      
      {currentScreen === 'levelGrade' && (
        <LevelGradeSelectionScreen
          onSelect={(level, grade) => {
            setSelectedEducationLevel(level);
            setSelectedGrade(grade);
            setCurrentScreen('reviewType');
          }}
          onBack={() => setCurrentScreen('instructions')}
        />
      )}
      
      {currentScreen === 'reviewType' && (
        <ReviewTypeSelectionScreen
          selectedType={selectedType}
          onSelect={(type) => {
            setSelectedType(type);
            if (type === 'testSchedule') {
              setCurrentScreen('testSchedule');
            } else {
              setCurrentScreen('reviewsList');
            }
          }}
          onBack={() => setCurrentScreen('levelGrade')}
        />
      )}
      
      {currentScreen === 'testSchedule' && (
        <TestScheduleScreen
          educationLevel={selectedEducationLevel}
          grade={selectedGrade}
          onBack={() => setCurrentScreen('reviewType')}
        />
      )}
      
      {currentScreen === 'reviewsList' && (
        <ReviewsListScreen
          educationLevel={selectedEducationLevel}
          grade={selectedGrade}
          type={selectedType}
          onBack={handleBackToSelection}
          onReset={handleReset}
          onViewFile={(url, fileName) => {
            setViewerFile({ url, fileName });
            setPreviousScreen('reviewsList');
            setCurrentScreen('fileViewer');
          }}
        />
      )}
      
      {currentScreen === 'fileViewer' && viewerFile && (
        <FileViewerScreen
          fileUrl={viewerFile.url}
          fileName={viewerFile.fileName}
          onBack={() => {
            if (previousScreen) {
              setCurrentScreen(previousScreen);
              setPreviousScreen(null);
            } else {
              setCurrentScreen('reviewType');
            }
            setViewerFile(null);
          }}
        />
      )}

      {showVisitorsScreen && (
        <VisitorsScreen onClose={() => {
          visitorsScreenRef.current = false;
          setShowVisitorsScreen(false);
        }} />
      )}
    </div>
  );
}

export default App;

